import { clsx } from "clsx";
import {
  createContext,
  FC,
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { graphql, useFragment } from "react-relay";
import { Routes, useLocation, useNavigate } from "react-router-dom";
import { Link, route } from "../Gate";
import { useWalletAccounts } from "../services/accounts";
import { Balances } from "./components/Balances";
import { BanxaDeepLinkHandler } from "../common/components/BanxaDeepLinkHandler";
import { BanxaReturnHandler } from "../common/components/BanxaReturnHandler";
//import { Merge } from "./components/Merge";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  Fiat,
  nFormatter,
  PrivacyGhost,
  Toggle,
  TranslationProvider,
  useGlobalModalContext,
  useLocalStorage,
  useTranslation,
} from "rujira.ui";

import { usePreloadedAccountData } from "../services/accountData";
import { AccountSelect } from "./components/AccountSelect";
import { Bonded } from "./components/Bonded";
//import { Buy, Deposit } from "./components/Cta";
import { Icons, useLocale } from "rujira.ui";
import { Deposit } from "../common/components/Deposit";
import { useIsMobile } from "../hooks/useBreakpoint";
import { NoIndexHelmet } from "../seo";
import {
  PortfolioFragment$data,
  PortfolioFragment$key,
} from "./__generated__/PortfolioFragment.graphql";
import { Connect } from "./components/Connect";
import { Merging } from "./components/Merging";
import { Orders } from "./components/Orders";
import { Positions } from "./components/Positions";
import { Strategies } from "./components/Strategies";
import { Streaming } from "./components/Streaming";
import { PositionScopes } from "./components/PositionScopes";
import { accountValue, isUnclaimedMerge } from "./utils";
import { usePortfolioBalanceValue } from "./usePortfolioBalanceValue";
import { RedactedScope, useRedacted } from "../services/redacted";
import { AccountDataContext } from "../services/accountData";
import { useRedactedV2 } from "../redacted/v2";
import PrivacyFlowModal from "./components/PrivacyFlowModal";
import { PortfolioToolbar } from "./components/PortfolioToolbar";
import { AssetFlow } from "./components/actions/AssetFlow";

export const PortfolioFragment = graphql`
  fragment PortfolioFragment on Account {
    ...MergePortfolioAccountFragment
    fin {
      ordersV2(first: 100)
        @connection(key: "TradeSubscriptionsFragment_ordersV2") {
        edges {
          cursor
          node {
            __typename
            ... on FinOrder {
              valueUsd
            }
            ... on CalcOrder {
              valueUsd
              balances {
                amount
              }
              ...RecurringOrdersFragment
            }
          }
        }
      }
      ranges(first: 100) @connection(key: "TradeSubscriptionsFragment_ranges") {
        edges {
          node {
            valueUsd
            ...TradeSubscriptionsFinRangeFragment
          }
        }
      }
    }

    merge {
      accounts {
        merged {
          amount
        }
        shares
        valueUsd
      }
    }

    staking {
      single {
        bonded {
          amount
        }
        liquid {
          amount
        }
      }
    }
    credit {
      accounts {
        id
        valueUsd
      }
    }
    strategies {
      __typename
      ... on ThorchainLiquidityProvider {
        id
        valueUsd
      }
      ... on BowAccount {
        id
        valueUsd
      }
      ... on StakingAccount {
        id
        valueUsd
      }
      ... on IndexAccount {
        id
        sharesValue
      }
      ... on GhostVaultAccount {
        id
        valueUsd
      }
    }
  }
`;

enum Tab {
  Balances = "",
  Merging = "merging",
  Streaming = "streaming",
  Bonded = "bonded",
  Strategies = "strategies",
  Lent = "lent",
  Borrowing = "borrowing",
  Orders = "orders",
}

const ASSET_ACTIONS = ["deposit", "withdraw", "send"] as const;
const isBalancesPath = (path: string, portfolioRoot: string) =>
  path === portfolioRoot ||
  path === `${portfolioRoot}/` ||
  ASSET_ACTIONS.some(
    (action) =>
      path === `${portfolioRoot}/${action}` ||
      path.startsWith(`${portfolioRoot}/${action}/`)
  );

export const Portfolio: FC = () => {
  const { selected } = useWalletAccounts();
  const { isPrivateMode } = useRedacted();

  return (
    <TranslationProvider namespace="portfolio">
      <Meta />
      {/* A fiat ramp link never opens a KYC provider flow in Private Mode,
          and without Banxa a return link is only a spoofable screen. */}
      {__BANXA_ENABLED__ && !isPrivateMode && <BanxaReturnHandler />}
      {!isPrivateMode && <BanxaDeepLinkHandler />}
      <PortfolioActionModal />

      {selected ? (
        <div className="rujira__main rujira__main--gradient">
          <Container>
            <MyAccount />
          </Container>
        </div>
      ) : (
        <Connect />
      )}
    </TranslationProvider>
  );
};

const PortfolioActionModal: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selected } = useWalletAccounts();
  const { isPrivateMode } = useRedacted();
  const { showModal, hideModal, isCurrentModal } = useGlobalModalContext();
  const { toRoot } = useLocale();
  const segments = location.pathname.replace(/\/+$/, "").split("/");
  const finalSegment = segments[segments.length - 1];
  const parentSegment = segments[segments.length - 2];
  const parentAction = ASSET_ACTIONS.find(
    (candidate) => candidate === parentSegment
  );
  const action =
    parentAction ||
    ASSET_ACTIONS.find((candidate) => candidate === finalSegment);
  const assetSegment = parentAction ? finalSegment : undefined;
  const selectedAddress = selected?.address.address;

  useEffect(() => {
    if (!action) return;

    if (!selectedAddress && action !== "deposit") {
      hideModal();
      return;
    }

    // Private Mode has no public send. Its withdrawal leaves the Private
    // Reserve for the connected wallet through the private flow, never
    // through the public asset flow.
    if (isPrivateMode && action === "send") return;

    const close = () => {
      hideModal();
      navigate(toRoot("portfolio"));
    };

    const modal = {
      className: "modal--lg",
      backgroundClose: false,
      // The route owns it: this effect reopens it for another wallet or view.
      keepOnAccountChange: true,
      onClose: close,
      children:
        action === "deposit" || (isPrivateMode && action === "withdraw") ? (
          <Deposit
            key={`${action}:${assetSegment || "overview"}`}
            dismiss={close}
            assetSegment={assetSegment}
            isWithdraw={action === "withdraw" || undefined}
          />
        ) : (
          <AssetFlow
            key={`${action}:${assetSegment || "overview"}`}
            action={action}
            close={close}
            assetSegment={assetSegment}
          />
        ),
    };
    showModal(modal);

    // Leaving the route closes this dialog only, never one opened on top of
    // it since (Private Mode sign-in, a consent or a transaction).
    return () => {
      if (isCurrentModal(modal)) hideModal();
    };
  }, [
    action,
    assetSegment,
    hideModal,
    isCurrentModal,
    isPrivateMode,
    navigate,
    selectedAddress,
    showModal,
    toRoot,
  ]);

  return null;
};

const Meta = () => {
  const { t } = useTranslation();
  return (
    <NoIndexHelmet>
      <title>{t("pageTitle")}</title>
      <meta name="description" content={t("pageDescription")} />
      <meta name="og:description" content={t("pageDescription")} />
      <meta name="twitter:description" content={t("pageDescription")} />
    </NoIndexHelmet>
  );
};

const Container: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  return (
    <div className="rujira__inner--pad portfolio">
      <div className="rujira__inner">
        <div className="row wrap pad ai-c">
          <div className="col-12 col-lg-5">
            <h1 className="h2">{t("myPortfolio")}</h1>
            <AccountSelect />
          </div>
          <PortfolioProvider>
            <Suspense fallback={<Fallback />}>{children}</Suspense>
          </PortfolioProvider>
        </div>
      </div>
    </div>
  );
};

const MyAccount: FC = () => {
  const { accountData } = usePreloadedAccountData();
  const { t } = useTranslation();
  const account = useFragment<PortfolioFragment$key>(
    PortfolioFragment,
    accountData
  );
  const { value: balanceValue, isLoading } = usePortfolioBalanceValue();

  const v2 = useRedactedV2();
  const { isPrivateMode } = useRedacted();
  // Model B: positions live in several subs; count them as one account.
  const unified = v2.modelB.enabled && isPrivateMode;
  const subs = unified ? v2.modelB.scopes : [];
  const [scoped, setScoped] = useState<Record<string, ScopedSummary>>({});
  const report = useCallback(
    (subwallet: string, summary: ScopedSummary) =>
      setScoped((current) =>
        summaryKey(current[subwallet]) === summaryKey(summary)
          ? current
          : { ...current, [subwallet]: summary }
      ),
    []
  );
  const scopedSummaries = subs.flatMap((subwallet) =>
    scoped[subwallet] ? [scoped[subwallet]] : []
  );

  if (isLoading) return <Fallback />;

  const positionsValue = unified
    ? scopedSummaries.reduce((sum, summary) => sum + summary.value, 0n)
    : account
      ? accountValue(account)
      : 0n;
  const value = balanceValue !== undefined ? balanceValue + positionsValue : 0n;

  const tabs = [
    {
      tab: Tab.Balances,
      label: t("balances"),
      value: balanceValue ?? 0n,
    },
    ...(unified
      ? mergeTabs(scopedSummaries.map((summary) => summary.tabs))
      : account
        ? accountTabs(account, t)
        : []),
  ];

  return (
    <>
      {subs.map((subwallet) => (
        <RedactedScope key={subwallet} subWalletAddress={subwallet}>
          <AccountDataContext>
            <Suspense fallback={null}>
              <ScopedAccountReport subwallet={subwallet} onReport={report} />
            </Suspense>
          </AccountDataContext>
        </RedactedScope>
      ))}
      <Content value={value} tabs={tabs} />
    </>
  );
};

type ScopedSummary = {
  tabs: ReturnType<typeof accountTabs>;
  value: bigint;
};

const summaryKey = (summary?: ScopedSummary) =>
  summary
    ? JSON.stringify(summary, (_key, value) =>
        typeof value === "bigint" ? value.toString() : value
      )
    : "";

/** Sum each tab's count and value across the subs of one private wallet. */
const mergeTabs = (lists: ReturnType<typeof accountTabs>[]) => {
  const merged = new Map<Tab, ReturnType<typeof accountTabs>[number]>();
  for (const list of lists)
    for (const tab of list) {
      const current = merged.get(tab.tab);
      merged.set(
        tab.tab,
        current
          ? {
              ...current,
              count: (current.count ?? 0) + (tab.count ?? 0),
              value: (current.value ?? 0n) + (tab.value ?? 0n),
            }
          : tab
      );
    }
  return [...merged.values()];
};

/** Reads one position sub's portfolio summary in its own data scope. */
const ScopedAccountReport: FC<{
  subwallet: string;
  onReport: (subwallet: string, summary: ScopedSummary) => void;
}> = ({ subwallet, onReport }) => {
  const { t } = useTranslation();
  const { accountData } = usePreloadedAccountData(false);
  const account = useFragment<PortfolioFragment$key>(
    PortfolioFragment,
    accountData
  );
  useEffect(() => {
    if (!account) return;
    onReport(subwallet, {
      tabs: accountTabs(account, t),
      value: accountValue(account),
    });
  }, [account, onReport, subwallet, t]);
  return null;
};

const accountTabs = (
  account: PortfolioFragment$data,
  t: (key: string) => string
) => [
  {
    tab: Tab.Merging,
    label: t("merging"),
    count: (account?.merge?.accounts || []).filter(isUnclaimedMerge).length,
    hideable: true,
    value: (account?.merge?.accounts || [])
      .filter(isUnclaimedMerge)
      .reduce((a, v) => a + BigInt(v?.valueUsd || 0), 0n),
  },
  // { tab: Tab.Streaming, label: t("streaming"), count: 1 },
  {
    tab: Tab.Orders,
    label: t("orders"),
    count:
      (account?.fin?.ordersV2?.edges?.filter(
        (o) =>
          !!o?.node &&
          (o.node.__typename === "FinOrder" ||
            (o.node.__typename === "CalcOrder" &&
              o.node.balances?.some((b) => Number(b?.amount) > 10)))
      ).length || 0) + (account.fin?.ranges?.edges?.length || 0),
    hideable: true,
    value: (account?.fin?.ordersV2?.edges || []).reduce(
      (a, v) =>
        a +
        BigInt(
          !!v?.node && "valueUsd" in v?.node && v?.node.valueUsd
            ? v?.node?.valueUsd
            : 0
        ),
      (account.fin?.ranges?.edges || []).reduce(
        (a, v) => (v?.node ? a + v?.node.valueUsd : a),
        0n
      )
    ),
  },
  {
    tab: Tab.Borrowing,
    label: t("borrowing"),
    count: account?.credit.accounts?.length,
    hideable: true,
    value: (account?.credit.accounts || []).reduce(
      (a, v) => a + BigInt(v.valueUsd),
      0n
    ),
  },
  {
    tab: Tab.Strategies,
    label: t("strategies"),
    count: account?.strategies?.length ?? 0,
    hideable: true,
    value: (account?.strategies || []).reduce((a, v) => {
      return (
        a +
        BigInt(
          v.__typename === "%other"
            ? 0
            : v.__typename === "IndexAccount"
              ? v.sharesValue
              : v?.valueUsd || 0
        )
      );
    }, 0n),
  },
];

const Content: FC<{
  value: bigint;
  tabs: {
    tab: Tab;
    label: string;
    count?: number;
    hideable?: boolean;
    value?: bigint;
  }[];
}> = ({ value, tabs }) => {
  const { t } = useTranslation();
  const chart = tabs.map((x) => ({
    name: x.label,
    value: Number(x.value) / 10 ** 8,
  }));

  const colors = ["#D615EB", "#7e0c8a", "#8436F5", "#5A2AD1", "#60fbd0"];

  return (
    <>
      <div className="col-12 col-lg-7 mt-2">
        <div className="card h-full p-3 flex wrap gap-1">
          <div className="flex dir-c grow">
            <h3 className="fs-16 lh-22 fw-400 color-grey">
              {t("common:walletValue")}
            </h3>
            <Fiat
              symbol="$"
              amount={value}
              decimals={8}
              className="fs-42 condensed fw-500 mt-1"
            />
          </div>
          {value > 0n && (
            <div className="w-10 h-10">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart width={80} height={80}>
                  <Pie
                    dataKey="value"
                    data={chart}
                    cx="50%"
                    cy="50%"
                    innerRadius={20}
                    outerRadius={40}
                    strokeWidth={0}
                    fill="#82ca9d">
                    {chart.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<CustomTooltip />}
                    allowEscapeViewBox={{ x: true, y: true }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="col-12">
        <Tabs tabs={tabs} />
      </div>
      <div className="col-12">
        <TabContent />
      </div>
    </>
  );
};

const Fallback: FC = () => {
  const { t } = useTranslation();
  const v2 = useRedactedV2();
  const { isPrivateMode } = useRedacted();
  // Model B keeps room for the Privacy column while loading.
  const unified = v2.modelB.enabled && isPrivateMode;
  return (
    <>
      <div className="col-12 col-lg-7 mt-2">
        <div className="card h-16 p-3">
          <h3 className="fs-16 lh-22 fw-400 color-grey">
            {t("common:walletValue")}
          </h3>
          <div className="skeleton h-6 w-24 br-10 mt-1.5 mb-2" />
        </div>
      </div>

      <div className="col-12">
        <div className="tabs mt-4">
          <a className="skeleton w-12" />
        </div>
      </div>
      <div className="col-12">
        <div className="relative card p-3 mt-2">
          <table className="table table--big condensed portfolio__balances table--no-hover">
            <tbody>
              {[...Array(3)].map((_, i) => (
                <tr key={`skeleton-${i}`}>
                  <td className="w-1">
                    <div className="skeleton h-4 w-4 br-4" />
                  </td>
                  <td>
                    <div className="skeleton h-2 w-12 br-2" />
                  </td>
                  {unified && (
                    <td className="auto">
                      <div className="skeleton h-3 br-1 privacy-meter__placeholder" />
                    </td>
                  )}
                  <td className="text-right">
                    <div className="skeleton h-2 w-10 br-2 inline-block" />
                  </td>
                  <td className="text-right">
                    <div className="skeleton h-2 w-10 br-2 inline-block" />
                  </td>
                  <td className="text-right">
                    <div className="skeleton h-2 w-6 br-2 inline-block" />
                  </td>
                  <td className="text-right">
                    <div className="skeleton h-2 w-10 br-2 inline-block" />
                  </td>
                  <td className="w-1">
                    <div className="skeleton h-4 w-4 br-0.5" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="card bg-darkGrey p-1.5 fs-14 condensed">
        <p className="label">
          {payload[0].name}
          <br />${payload[0].value.toLocaleDecimal(2)}
        </p>
      </div>
    );
  }
  return null;
};

const Tabs: FC<{
  tabs: {
    tab: Tab;
    label: string;
    count?: number;
    hideable?: boolean;
    value?: bigint;
  }[];
}> = ({ tabs }) => {
  const { isPrivateMode } = useRedacted();
  const { selected } = useWalletAccounts();
  const { hideSmall, setHideSmall } = usePortfolio();
  const loc = useLocation();
  const { toRoot } = useLocale();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const portfolioRoot = toRoot("portfolio");
  const currentPath = loc.pathname.replace(/\/+$/, "");
  const isBalances = isBalancesPath(currentPath, portfolioRoot);
  const { showModal } = useGlobalModalContext();
  const [guideHover, setGuideHover] = useState(false);
  const showPrivacyGuide = () =>
    showModal({
      title: "",
      className: "modal--lg privacy-guide-modal",
      backgroundClose: true,
      children: <PrivacyFlowModal />,
    });
  const visibleTabs = tabs.filter(
    (tab) => tab.hideable !== true || tab.count !== 0
  );
  const isSelected = (tab: Tab) =>
    tab === Tab.Balances
      ? isBalances
      : currentPath === `${portfolioRoot}/${tab}`;
  const selectedTab =
    visibleTabs.find(({ tab }) => isSelected(tab)) ?? visibleTabs[0];

  const label = (item: (typeof tabs)[number], structured = false) => (
    <div
      className={clsx("portfolio-tabs__label", {
        "portfolio-tabs__label--structured": structured,
        "flex row ai-c": !structured,
      })}>
      <span>{item.label}</span>
      {structured &&
        (item.count ? (
          <span className="portfolio-tabs__count">{item.count}</span>
        ) : (
          <span />
        ))}
      {item.value !== undefined && item.value > 0n ? (
        <div className={clsx("tag tag--teal tag--md", { "ml-1": !structured })}>
          ${nFormatter(item.value || 0n, 2)}
        </div>
      ) : (
        structured && <span aria-hidden="true" />
      )}
      {!structured && item.count ? <i>{item.count}</i> : null}
    </div>
  );

  return (
    <PortfolioToolbar
      className="portfolio-tabs"
      tabs={
        isMobile && selectedTab ? (
          <div className="selected bg-black flex jc-sb">
            {label(selectedTab)}
            <Icons.AngleDown className="color-white" />
            <nav>
              {visibleTabs
                .filter(({ tab }) => !isSelected(tab))
                .map((item) => (
                  <TabItem key={item.label} tab={item.tab}>
                    {label(item, true)}
                  </TabItem>
                ))}
            </nav>
          </div>
        ) : (
          visibleTabs.map((item) => (
            <TabItem key={item.label} tab={item.tab}>
              {label(item)}
            </TabItem>
          ))
        )
      }>
      {isPrivateMode && (
        <button
          type="button"
          className="transparent ml-a mr-0 w-4 h-6 flex ai-c jc-c as-e"
          onClick={showPrivacyGuide}
          onMouseOver={() => setGuideHover(true)}
          onMouseOut={() => setGuideHover(false)}
          data-tooltip-id="global-tip"
          data-tooltip-content="How Private Mode works">
          <PrivacyGhost
            motion={guideHover ? "wink" : "still"}
            alt="Private Mode guide"
            className="w-2 h-2 filter-grey"
          />
        </button>
      )}

      {/* Private Mode shows no explorer link: it would hand the Spending
          address to the explorer and to anyone watching the traffic. */}
      {!isPrivateMode && (
        <a
          className="ml-a mr-0"
          href={`https://thorchain.net/address/${selected?.address.address ?? ""}`}
          target="_blank"
          rel="noopener noreferrer">
          {t("common:history")}
          <Icons.External className="ml-0.5" />
        </a>
      )}
      {isBalances && (
        <Toggle
          label={
            isMobile
              ? t("common:hideLowBalance")
              : t("common:hideLowValueBalances")
          }
          className="toggle--xs color-white as-c ml-1"
          checked={hideSmall}
          onChange={(e) => setHideSmall(e.currentTarget.checked)}
        />
      )}
    </PortfolioToolbar>
  );
};

const TabItem: FC<
  PropsWithChildren<{
    tab: Tab;
    counter?: number;
  }>
> = ({ tab, counter, children }) => {
  const loc = useLocation();
  const { toRoot } = useLocale();
  const path = loc.pathname.replace(/\/+$/, "");
  const portfolioRoot = toRoot("portfolio");
  const isSelected =
    tab === Tab.Balances
      ? isBalancesPath(path, portfolioRoot)
      : path === `${portfolioRoot}/${tab}`;

  return (
    <Link
      to={tab === Tab.Balances ? "." : tab}
      className={clsx({ "selected bg-black": isSelected })}>
      {children} {counter ? <i>{counter}</i> : null}
    </Link>
  );
};

const TabContent: FC = () => (
  <Routes>
    {route({
      path: "",
      element: <Balances />,
    })}
    {ASSET_ACTIONS.flatMap((path) => [
      route({ path, element: <Balances /> }),
      route({ path: `${path}/:asset`, element: <Balances /> }),
    ])}
    {route({
      path: Tab.Streaming,
      element: <Streaming />,
    })}
    {route({
      path: Tab.Merging,
      element: <Merging />,
    })}

    {route({
      path: Tab.Orders,
      element: (
        <PositionScopes>
          <Orders />
        </PositionScopes>
      ),
    })}

    {route({
      path: Tab.Strategies,
      element: (
        <PositionScopes>
          <Strategies />
        </PositionScopes>
      ),
    })}

    {route({
      path: Tab.Bonded,
      element: (
        <PositionScopes>
          <Bonded />
        </PositionScopes>
      ),
    })}

    {route({
      path: Tab.Borrowing,
      element: (
        <PositionScopes>
          <Positions />
        </PositionScopes>
      ),
    })}

    {route({
      path: "*",
      element: <ComingSoon />,
    })}
  </Routes>
);

const ComingSoon: FC = () => {
  const { t } = useTranslation();
  return (
    <div className="card text-center p-3 mt-2 condensed color-grey">
      {t("common:comingSoon")}
    </div>
  );
};

interface PortfolioContextType {
  hideSmall: boolean;
  setHideSmall: (value: boolean) => void;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(
  undefined
);

export const PortfolioProvider: FC<PropsWithChildren> = ({ children }) => {
  const [hideSmall, setHideSmall] = useLocalStorage(
    "rujira-portfolio-small",
    false
  );

  return (
    <PortfolioContext.Provider value={{ hideSmall, setHideSmall }}>
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error("usePortfolio must be used within a PortfolioProvider");
  }
  return context;
};
